import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessType } from '@/contexts/BusinessTypeContext';
import api from '@/lib/api';
import { formatErrorMessage } from '@/lib/error-utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, EyeOff, UtensilsCrossed, Upload, Download, FileSpreadsheet, CheckCircle, AlertTriangle } from 'lucide-react';
import { formatCurrency } from '@/lib/utils-helpers';

export default function VendorMenu() {
  const navigate = useNavigate();
  const { config } = useBusinessType();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', price: '', category: 'General', category_node_id: null, category_path: '', image_url: '' });
  const [filter, setFilter] = useState('all');
  
  // CSV Import states
  const [showImport, setShowImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [importHistory, setImportHistory] = useState([]);

  const fetchMenu = async () => {
    setLoading(true);
    try {
      const [menuRes, catRes, treeRes] = await Promise.all([
        api.get('/vendor/menu'), 
        api.get('/vendor/categories'),
        api.get('/vendor/inventory/category-tree')
      ]);
      setItems(menuRes.data.items || []);
      setCategories(catRes.data.categories || []);
      setCategoryTree(treeRes.data.tree || []);
    } catch (err) { 
      toast.error(formatErrorMessage(err, 'Load menu'));
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMenu(); }, []);

  const resetForm = () => setForm({ name: '', description: '', price: '', category: 'General', category_node_id: null, category_path: '', image_url: '' });

  const handleAdd = async () => {
    if (!form.name || !form.price) { toast.error('Name and price required'); return; }
    try {
      await api.post('/vendor/menu', { ...form, price: Number(form.price) });
      toast.success('Item added'); setShowAdd(false); resetForm(); fetchMenu();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/vendor/menu/${editItem.id}`, { ...form, price: Number(form.price) });
      toast.success('Item updated'); setEditItem(null); resetForm(); fetchMenu();
    } catch (err) { 
      toast.error(formatErrorMessage(err, 'Update item'));
    }
  };

  const handleDelete = async () => {
    try { 
      await api.delete(`/vendor/menu/${deleteItem.id}`); 
      toast.success('Item deleted'); 
      setDeleteItem(null); 
      fetchMenu(); 
    } catch (err) { 
      toast.error(formatErrorMessage(err, 'Delete item'));
    }
  };

  const toggleAvailability = async (item) => {
    try { 
      await api.put(`/vendor/menu/${item.id}/toggle`); 
      toast.success(`${item.name} ${item.is_available ? 'hidden' : 'available'}`); 
      fetchMenu(); 
    } catch (err) { 
      toast.error(formatErrorMessage(err, 'Toggle availability'));
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    
    // Refresh category tree to get latest categories
    api.get('/vendor/inventory/category-tree')
      .then(response => {
        const freshTree = response.data.tree || [];
        setCategoryTree(freshTree);
        
        // Try to find matching category node
        let categoryNodeId = item.category_node_id || null;
        let categoryPath = item.category_path || '';
        
        // If no category_node_id, try to match by category name or path
        if (!categoryNodeId && item.category && item.category !== 'General') {
          const findNodeByName = (nodes, categoryName) => {
            for (const node of nodes) {
              // Match by leaf name or full path
              if (node.name === categoryName || node.path === categoryName) {
                return node;
              }
              if (node.children && node.children.length > 0) {
                const found = findNodeByName(node.children, categoryName);
                if (found) return found;
              }
            }
            return null;
          };
          
          const matchedNode = findNodeByName(freshTree, item.category);
          if (matchedNode) {
            categoryNodeId = matchedNode.id;
            categoryPath = matchedNode.path;
          }
        }
        
        setForm({ 
          name: item.name, 
          description: item.description || '', 
          price: String(item.price), 
          category: item.category || 'General',
          category_node_id: categoryNodeId,
          category_path: categoryPath,
          image_url: item.image_url || '' 
        });
      })
      .catch(err => {
        console.error('Failed to refresh categories:', err);
        // Still set the form even if category refresh fails
        setForm({ 
          name: item.name, 
          description: item.description || '', 
          price: String(item.price), 
          category: item.category || 'General',
          category_node_id: item.category_node_id || null,
          category_path: item.category_path || '',
          image_url: item.image_url || '' 
        });
      });
  };

  // Build flat list of categories for dropdown
  const buildCategoryOptions = (nodes, prefix = '') => {
    let options = [];
    for (const node of nodes) {
      const label = prefix ? `${prefix} > ${node.name}` : node.name;
      options.push({ 
        value: node.id, 
        label: label, 
        path: node.path,
        name: node.name
      });
      if (node.children && node.children.length > 0) {
        options = options.concat(buildCategoryOptions(node.children, label));
      }
    }
    return options;
  };

  const categoryOptions = [
    { value: 'none', label: 'General (No Category)', path: '', name: 'General' },
    ...buildCategoryOptions(categoryTree)
  ];

  const handleCategoryChange = (value) => {
    if (value === 'none') {
      setForm({ ...form, category: 'General', category_node_id: null, category_path: '' });
    } else {
      const selected = categoryOptions.find(opt => opt.value === value);
      if (selected) {
        setForm({ 
          ...form, 
          category: selected.name, 
          category_node_id: selected.value,
          category_path: selected.path
        });
      }
    }
  };

  // CSV Import Functions
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/vendor/menu/csv-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'r2yn_menu_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleExportMenu = async () => {
    try {
      const response = await api.get('/vendor/menu/csv-export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `menu_export_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Menu exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      toast.error(formatErrorMessage(err, 'Export menu'));
    }
  };

  const fetchImportHistory = async () => {
    try {
      const response = await api.get('/vendor/menu/import-history');
      setImportHistory(response.data.imports || []);
    } catch (err) {
      toast.error('Failed to load import history');
    }
  };

  const handleShowHistory = () => {
    fetchImportHistory();
    setShowHistory(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setCsvFile(file);
      setImportPreview(null);
    }
  };

  const handlePreviewImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', csvFile);
    
    try {
      const response = await api.post('/vendor/menu/csv-import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportPreview(response.data);
      toast.success(`Preview ready: ${response.data.valid_rows} valid, ${response.data.invalid_rows} invalid`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleCommitImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', csvFile);
    
    try {
      const response = await api.post('/vendor/menu/csv-import/commit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Import complete: ${response.data.items_created} created, ${response.data.items_updated} updated`);
      setShowImport(false);
      setCsvFile(null);
      setImportPreview(null);
      fetchMenu();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const filteredItems = filter === 'all' ? items : filter === 'available' ? items.filter(i => i.is_available) : items.filter(i => !i.is_available);
  const uniqueCategories = [...new Set(items.map(i => i.category).filter(Boolean))];

  // Render the form inline to prevent re-creation issues
  const renderItemForm = (onSubmit, title) => (
    <>
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>Name</Label>
          <Input 
            value={form.name} 
            onChange={e => setForm({...form, name: e.target.value})} 
            placeholder="Margherita Pizza" 
            data-testid="menu-item-name-input" 
          />
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea 
            value={form.description} 
            onChange={e => setForm({...form, description: e.target.value})} 
            placeholder="Describe the item..." 
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Price ($)</Label>
            <Input 
              type="number" 
              step="0.01" 
              value={form.price} 
              onChange={e => setForm({...form, price: e.target.value})} 
              data-testid="menu-item-price-input" 
            />
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select 
              value={form.category_node_id || 'none'} 
              onValueChange={handleCategoryChange}
            >
              <SelectTrigger data-testid="category-select">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.length === 1 ? (
                  <SelectItem value="none">No categories created yet</SelectItem>
                ) : (
                  categoryOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {form.category_path && (
              <p className="text-xs text-muted-foreground mt-1">
                Path: {form.category_path}
              </p>
            )}
          </div>
        </div>
        <div className="space-y-2">
          <Label>Image URL (optional)</Label>
          <Input 
            value={form.image_url} 
            onChange={e => setForm({...form, image_url: e.target.value})} 
            placeholder="https://..." 
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => { setShowAdd(false); setEditItem(null); resetForm(); }}>Cancel</Button>
        <Button onClick={onSubmit} data-testid="menu-item-save-button">Save</Button>
      </DialogFooter>
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">{config.catalogLabel}</h1>
          <p className="text-muted-foreground mt-1">Add, edit, and manage your {config.itemsLabel.toLowerCase()}.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportMenu} data-testid="export-csv-button">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button variant="outline" onClick={handleShowHistory} data-testid="import-history-button">
            <FileSpreadsheet className="h-4 w-4 mr-2" /> History
          </Button>
          <Button variant="outline" onClick={() => setShowImport(true)} data-testid="import-csv-button">
            <Upload className="h-4 w-4 mr-2" /> Import CSV
          </Button>
          <Button onClick={() => { resetForm(); setShowAdd(true); }} data-testid="add-menu-item-button">
            <Plus className="h-4 w-4 mr-2" /> {config.addItemLabel}
          </Button>
        </div>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')}>All ({items.length})</Button>
        <Button variant={filter === 'available' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('available')}>Available ({items.filter(i => i.is_available).length})</Button>
        <Button variant={filter === 'unavailable' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('unavailable')}>Hidden ({items.filter(i => !i.is_available).length})</Button>
      </div>

      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32" />)}</div>
          ) : filteredItems.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredItems.map(item => (
                <div 
                  key={item.id} 
                  className={`p-4 rounded-xl border ${item.is_available ? 'bg-card' : 'bg-muted/50 opacity-75'} cursor-pointer hover:shadow-md transition-shadow`} 
                  data-testid={`menu-item-${item.id}`}
                  onClick={() => navigate(`/vendor/menu/${item.id}`)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <UtensilsCrossed className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium text-sm">{item.name}</h4>
                        {item.category_path ? (
                          <Badge variant="outline" className="text-xs" title={item.category_path}>
                            {item.category_path.length > 25 ? item.category_path.substring(0, 25) + '...' : item.category_path}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">{item.category}</Badge>
                        )}
                      </div>
                    </div>
                    <span className="text-lg font-semibold tabular-nums" data-font="display">{formatCurrency(item.price)}</span>
                  </div>
                  {item.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{item.description}</p>}
                  <div className="flex items-center justify-between">
                    <button 
                      className="flex items-center gap-1.5 text-xs" 
                      onClick={(e) => { e.stopPropagation(); toggleAvailability(item); }} 
                      data-testid={`toggle-item-${item.id}`}
                    >
                      {item.is_available ? <Eye className="h-3.5 w-3.5 text-emerald-600" /> : <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />}
                      <span className={item.is_available ? 'text-emerald-600' : 'text-muted-foreground'}>{item.is_available ? 'Available' : 'Hidden'}</span>
                    </button>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7" 
                        onClick={(e) => { e.stopPropagation(); openEdit(item); }}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 text-destructive" 
                        onClick={(e) => { e.stopPropagation(); setDeleteItem(item); }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-center py-8 text-muted-foreground">No menu items found</p>}
        </CardContent>
      </Card>

      {/* Add/Edit Dialogs */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          {renderItemForm(handleAdd, "Add Menu Item")}
        </DialogContent>
      </Dialog>
      <Dialog open={!!editItem} onOpenChange={() => { setEditItem(null); resetForm(); }}>
        <DialogContent>
          {renderItemForm(handleEdit, "Edit Menu Item")}
        </DialogContent>
      </Dialog>
      
      {/* Delete Dialog */}
      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Item</AlertDialogTitle><AlertDialogDescription>Remove {deleteItem?.name} from your menu?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CSV Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Menu from CSV</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Step 1: Download Template */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4" /> Step 1: Download Template
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Button variant="outline" onClick={handleDownloadTemplate} className="w-full" data-testid="download-csv-template">
                  <Download className="h-4 w-4 mr-2" /> Download CSV Template
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  Template includes: item_name, description, price, category_path, variants, modifiers, stock, image_url, prep_time, dietary_tags, availability
                </p>
              </CardContent>
            </Card>

            {/* Step 2: Upload File */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Upload className="h-4 w-4" /> Step 2: Upload Your CSV
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input type="file" accept=".csv" onChange={handleFileSelect} data-testid="csv-file-input" />
                {csvFile && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Selected: {csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
                <Button onClick={handlePreviewImport} disabled={!csvFile || importing} className="w-full mt-3" data-testid="preview-csv-button">
                  {importing ? 'Processing...' : 'Preview Import'}
                </Button>
              </CardContent>
            </Card>

            {/* Step 3: Preview Results */}
            {importPreview && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-600" /> Step 3: Review & Confirm
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-950 rounded-lg">
                      <div className="text-2xl font-bold text-emerald-600">{importPreview.valid_rows}</div>
                      <div className="text-xs text-muted-foreground">Valid Rows</div>
                    </div>
                    <div className="text-center p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">{importPreview.invalid_rows}</div>
                      <div className="text-xs text-muted-foreground">Invalid Rows</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">{importPreview.unique_categories}</div>
                      <div className="text-xs text-muted-foreground">Categories</div>
                    </div>
                  </div>

                  {importPreview.invalid_rows > 0 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>{importPreview.invalid_rows} rows have errors</strong> and will be skipped during import.
                        {importPreview.invalid_row_details?.slice(0, 3).map((err, idx) => (
                          <div key={idx} className="text-xs mt-1">• Row {err.row_number}: {err.errors.join(', ')}</div>
                        ))}
                      </AlertDescription>
                    </Alert>
                  )}

                  {importPreview.category_paths?.length > 0 && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Category Tree Preview:</Label>
                      <div className="text-xs bg-muted p-2 rounded mt-1 max-h-24 overflow-y-auto">
                        {importPreview.category_paths.slice(0, 10).map((path, idx) => (
                          <div key={idx}>• {path}</div>
                        ))}
                        {importPreview.category_paths.length > 10 && <div className="text-muted-foreground">+ {importPreview.category_paths.length - 10} more...</div>}
                      </div>
                    </div>
                  )}

                  <Button onClick={handleCommitImport} disabled={importing || importPreview.valid_rows === 0} className="w-full" data-testid="commit-csv-import">
                    {importing ? 'Importing...' : `Import ${importPreview.valid_rows} Items`}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImport(false); setCsvFile(null); setImportPreview(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import History</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {importHistory.length > 0 ? (
              importHistory.map((imp) => (
                <Card key={imp.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{imp.filename}</span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Uploaded by {imp.uploader_name} • {new Date(imp.created_at).toLocaleString()}
                        </div>
                        <div className="flex gap-4 text-xs mt-2">
                          <span className="text-emerald-600">✓ {imp.items_created} created</span>
                          <span className="text-blue-600">↻ {imp.items_updated} updated</span>
                          {imp.invalid_rows > 0 && (
                            <span className="text-red-600">✗ {imp.invalid_rows} invalid</span>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline">{imp.total_rows} rows</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-8">No import history yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
